import { ISelectOption } from '@common/components/forms';
import { StringUtility } from '@common/utils';
import { DateTime } from 'luxon';
import {
  BreedModel,
  GenderModel,
  IBreedModel,
  IClientModel,
  IGenderModel,
  IModel,
  IPetAlertPetModel,
  IPetColorModel,
  IPhotoModel,
  ISpeciesModel,
  Model,
  PetAlertPetModel,
  PetColorModel,
  PhotoModel,
  SpeciesModel,
} from '.';
import { EntityMappingModel, IEntityMappingModel } from './EntityMappingModel';
import { IPetNoteModel, PetNoteModel } from './PetNoteModel';
import { makeObservable, observable } from 'mobx';
import {
  IPetCustomReminderModel,
  PetCustomReminderModel,
} from '@common/models/PetCustomReminders';
import { ISyncModel, SyncModel } from '@common/models/SyncModel';

export interface IPetModel extends ISyncModel {
  id?: number;
  status?: number;
  alerts?: string;
  birthDate?: string;
  breed?: IBreedModel;
  breedId?: number | null;
  breedFullName: string;
  clientId: number;
  firstVisitDate?: string;
  gender?: IGenderModel;
  genderId?: number;
  hasAlerts: boolean;
  isActive?: boolean;
  isDeceased: boolean;
  isMixedBreed: boolean;
  lastVisitDate?: string;
  medications?: string;
  dateCreated: string;
  dateUpdated: string;
  // Needs to be StorageContent
  medicalHistoryDocument?: any;
  medicalHistoryDocumentId?: number;
  microchipNumber?: string;
  name: string;
  petAlerts: IPetAlertPetModel[];
  petColor?: IPetColorModel;
  petColorId?: number;
  petId: number;
  photoId?: number;
  photo?: IPhotoModel;
  rabiesTagNumber?: string;
  speciesId?: number;
  species?: ISpeciesModel;
  weight?: number;
  lastAppointmentType?: string;
  notes: IPetNoteModel[];
  entityMappings: IEntityMappingModel[];
  petCustomReminders: IPetCustomReminderModel[];
  owner: IClientModel;
}

export class PetModel extends SyncModel implements IPetModel {
  @observable alerts?: string;
  @observable birthDate?: string;
  @observable breed: IBreedModel;
  @observable breedId?: number | undefined | null;
  @observable breedFullName: string;
  @observable clientId: number;
  @observable firstVisitDate?: string;
  @observable gender: IGenderModel;
  @observable genderId?: number | undefined;
  @observable hasAlerts: boolean;
  @observable isActive?: boolean | undefined;
  @observable isDeceased: boolean;
  @observable isMixedBreed: boolean;
  @observable lastVisitDate?: string;
  @observable medications?: string;
  @observable medicalHistoryDocument?: any;
  @observable medicalHistoryDocumentId?: number;
  @observable microchipNumber?: string;
  @observable name: string;
  @observable petAlerts: IPetAlertPetModel[] = [];
  @observable petColor: IPetColorModel;
  @observable petColorId?: number | undefined;
  @observable petId: number;
  @observable photoId?: number | undefined;
  @observable photo?: IPhotoModel;
  @observable rabiesTagNumber?: string;
  @observable speciesId?: number | undefined;
  @observable species?: ISpeciesModel;
  @observable weight?: number | undefined;
  @observable dateCreated: string;
  @observable dateUpdated: string;
  @observable lastAppointmentType?: string;
  @observable notes: IPetNoteModel[] = [];
  @observable entityMappings: IEntityMappingModel[] = [];
  @observable petCustomReminders: IPetCustomReminderModel[] = [];
  @observable owner: IClientModel;

  constructor(params: Partial<IPetModel> = {}) {
    super(params);
    const {
      alerts = '',
      birthDate = undefined,
      breed = {} as IBreedModel,
      breedId = undefined,
      breedFullName = '',
      clientId = 0,
      firstVisitDate = undefined,
      gender = {} as IGenderModel,
      genderId = undefined,
      hasAlerts = false,
      isActive = true, // Default to true
      isDeceased = false,
      isMixedBreed = false,
      lastVisitDate = undefined,
      medications = '',
      medicalHistoryDocument = undefined,
      medicalHistoryDocumentId = undefined,
      microchipNumber = '',
      name = '',
      petAlerts = [] as Array<IPetAlertPetModel>,
      petColorId = undefined,
      petColor = {} as IPetColorModel,
      petId = 0,
      photoId = undefined,
      photo = {} as IPhotoModel,
      rabiesTagNumber = '',
      speciesId = undefined,
      species = {} as ISpeciesModel,
      weight = 0, // default to 0
      dateCreated = '',
      dateUpdated = '',
      lastAppointmentType = '',
      notes = [] as Array<IPetNoteModel>,
      entityMappings = [] as Array<IEntityMappingModel>,
      petCustomReminders = [] as Array<IPetCustomReminderModel>,
      owner = {} as IClientModel,
    } = params;

    const sanitizedBirthDate = StringUtility.SanitizeString(birthDate);

    this.alerts = StringUtility.SanitizeString(alerts);
    this.birthDate = sanitizedBirthDate
      ? DateTime.fromISO(sanitizedBirthDate).toISODate()
      : undefined;
    this.breed = breed && new BreedModel(breed);
    this.breedId = breedId;
    this.breedFullName = breedFullName;
    this.clientId = clientId;
    this.firstVisitDate = firstVisitDate;
    this.gender = gender && new GenderModel(gender);
    this.genderId = genderId;
    this.hasAlerts = hasAlerts;
    this.isActive = isActive;
    this.isDeceased = isDeceased;
    this.isMixedBreed = isMixedBreed;
    this.lastVisitDate = lastVisitDate;
    this.medications = medications;
    this.medicalHistoryDocument = medicalHistoryDocument;
    this.medicalHistoryDocumentId = medicalHistoryDocumentId;
    this.microchipNumber = StringUtility.SanitizeString(microchipNumber);
    this.name = name;
    this.petAlerts = (petAlerts ?? []).map((pa) => new PetAlertPetModel(pa));
    this.petColorId = petColorId;
    this.petColor = petColor && new PetColorModel(petColor);
    this.petId = petId;
    this.photoId = photoId;
    this.photo = photo && new PhotoModel(photo);
    this.rabiesTagNumber = StringUtility.SanitizeString(rabiesTagNumber);
    this.speciesId = speciesId;
    this.species = species && new SpeciesModel(species);
    this.weight = weight;
    this.dateCreated = dateCreated;
    this.dateUpdated = dateUpdated;
    this.lastAppointmentType = lastAppointmentType;
    this.notes = (notes ?? []).map((n) => new PetNoteModel(n));
    this.entityMappings = (entityMappings ?? []).map(
      (e) => new EntityMappingModel(e),
    );
    this.petCustomReminders = (petCustomReminders ?? []).map(
      (pcr) => new PetCustomReminderModel(pcr),
    );
    this.owner = owner;
    // TODO - MS: Computed
    // age: number -> calculated off of birthdate
    // ageYears: number;
    // ageMonths: number;
    // ageWeeks: number;
    // Computed
    //genderAbbreviation: string;
    // UI Property
    //friendlyBirthDate: string;
    // UI Property
    //isNewPet: boolean;
    makeObservable(this);
  }
}

export const getBreedLabel = (pet: IPetModel) => {
  return pet.breed && pet.isMixedBreed
    ? `${pet.breed.name} Mix`
    : pet.breed?.name;
};

export const petStatusOptions: ISelectOption[] = [
  { value: 1, label: 'Active' },
  { value: 2, label: 'Inactive' },
  { value: 3, label: 'Deceased' },
];
